import React, {useEffect, useMemo, useState} from 'react';
import './InformationalFooter.scss';
import MeasurementSvgIcon from './MeasurementSvgIcon';
import {useAppConfig} from '@mc/react-app-base';
import {Button, Callout, Dialog, DialogBody, DialogFooter, Icon, Menu, MenuItem} from '@mc/ui-v4';
import {mcDanger, mcInfo} from '@mc/react-ui-icons';
import {useMapioContext} from '../../mapioContext';
import {ErrorReporter} from './ErrorReporter';
import {DistanceData} from './ZoomSelector';
import {TopicVO} from '../../api';
import {UseQueryResult} from 'react-query';
import {useLayersConfig} from '../../hooks/useLayersConfig';
import {useTopicsMap} from '../../hooks/useTopicsMap';
import {useIsMobile} from '../../hooks/useIsMobile';

type FooterProps = {
    distanceData: DistanceData;
};

/** Skloňování počtu chyb v souhrnu dialogu (1 chyba / 2-4 chyby / 5+ chyb). */
const formatErrorCount = (count: number): string => {
    if (count === 1) {
        return '1 chyba';
    }
    if (count >= 2 && count <= 4) {
        return `${count} chyby`;
    }
    return `${count} chyb`;
};

export const InformationalFooter: React.FC<FooterProps> = ({distanceData}) => {
    const [toggleErrorDetail, setToggleErrorDetail] = useState<string | null>(null);
    const [isErrorsDialogOpen, setIsErrorsDialogOpen] = useState<boolean>(false);
    const isMobile = useIsMobile();
    const {version} = useAppConfig();
    const [attributions, setAttributions] = useState<{url: string; label: string}[]>([]);
    const mapioContext = useMapioContext();
    const {errors, topicId} = mapioContext.value;
    const layersQuery = useLayersConfig();
    const topicsMapQuery: UseQueryResult<TopicVO[]> = useTopicsMap();

    useEffect(() => {
        if (topicsMapQuery.data && layersQuery.data) {
            const currentTopic = topicsMapQuery.data.find(t => t.id === topicId);
            if (!currentTopic) return;

            const backgroundLayers = currentTopic.backgroundLayers || [];
            const foregroundLayers = currentTopic.foregroundLayers || [];
            const catalogItems = currentTopic.catalogItems || [];
            // Přidáme do setu všechny vrstvy, které se mají zobrazit
            const layerIds = new Set([...backgroundLayers, ...foregroundLayers, ...catalogItems]);
            const attributionMap = Array.from(layerIds).reduce((acc, layerId) => {
                const layer = layersQuery.data[layerId];
                if (layer?.attributionUrl) {
                    acc.set(layer.attributionUrl, {url: layer.attributionUrl, label: layer.attribution});
                }
                return acc;
            }, new Map<string, {url: string; label: string}>());

            setAttributions(Array.from(attributionMap.values()));
        }
    }, [topicsMapQuery.data, layersQuery.data, topicId]);

    // Počet chyb odvozujeme přímo z kontextu – dřív se držel ve stavu a plnil se ve smyčce
    // nad stejnou (zastaralou) hodnotou, takže místo součtu vycházel count poslední chyby.
    // Odvozená hodnota se navíc korektně vynuluje, když chyby zmizí.
    const errorCount = useMemo(() => errors.reduce((sum, error) => sum + (error.count || 0), 0), [errors]);
    const hasErrors = errors.length > 0;

    // Když chyby zmizí, zmizí i tlačítko – dialog musí jít s ním, jinak by zůstal
    // otevřený nad prázdným seznamem a při příchodu další chyby by vyskočil sám.
    useEffect(() => {
        if (!hasErrors) {
            setIsErrorsDialogOpen(false);
        }
    }, [hasErrors]);

    const errorCards = errors.map(error => (
        <ErrorReporter key={error.id} error={error} setToggleErrorDetail={setToggleErrorDetail} toggleErrorDetail={toggleErrorDetail} />
    ));

    return (
        <div className={'drop-shadow attribution-footer px-1'}>
            <div className="attribution-footer-content">
                <span className="pr-2">
                    {distanceData.currentDistance} <MeasurementSvgIcon stroke={'#294172'} distance={distanceData.currentPixelDistance} />
                </span>{' '}
                <span>&copy;&nbsp;</span>{' '}
                {attributions?.map((attribution, index) => (
                    <span key={`${index}-${attribution.url}`}>
                        {index > 0 && ', '}
                        <a href={attribution.url} target="_blank" rel="noreferrer" className="footer-link">
                            {attribution.label}
                        </a>
                    </span>
                ))}
            </div>
            <div className="attribution-footer-actions">
                <Button
                    style="link"
                    className={'info-button'}
                    dropdown
                    icon={<Icon icon={mcInfo} />}
                    menu={
                        <div className="p-2">
                            <div>Sestavení: {version}</div>
                            <div>Téma: {topicId}</div>
                        </div>
                    }
                />
                {hasErrors &&
                    // Na mobilu se seznam chyb otevírá v dialogu – dropdown je na malém
                    // displeji nepoužitelně stísněný. Na desktopu zůstává rozbalovací menu.
                    (isMobile ? (
                        <Button
                            danger
                            className={'info-button'}
                            icon={<Icon icon={mcDanger} />}
                            title={'Chyby při načítání dat'}
                            onClick={() => setIsErrorsDialogOpen(true)}
                        >
                            {errorCount > 0 ? errorCount : undefined}
                        </Button>
                    ) : (
                        <Button
                            danger
                            className={'info-button'}
                            // Ikona se musí zobrazit vždy, když jsou chyby – dřív ji podmínka
                            // `!errorCount` naopak schovala právě v okamžiku, kdy chyba přišla.
                            icon={<Icon icon={mcDanger} />}
                            hideMenuDropdownIcon
                            title={'Chyby při načítání dat'}
                            popupProps={{
                                closeOnInnerClick: false,
                            }}
                            menu={
                                <Menu className="p-2 error-menu">
                                    {errors.map(error => (
                                        <MenuItem
                                            key={error.id}
                                            text={
                                                <ErrorReporter
                                                    key={error.id}
                                                    error={error}
                                                    setToggleErrorDetail={setToggleErrorDetail}
                                                    toggleErrorDetail={toggleErrorDetail}
                                                    alwaysExpanded
                                                />
                                            }
                                        ></MenuItem>
                                    ))}
                                </Menu>
                            }
                        >
                            {errorCount > 0 ? errorCount : undefined}
                        </Button>
                    ))}
            </div>
            {isMobile && hasErrors && isErrorsDialogOpen && (
                <Dialog size={'fullscreen'} title={'Chyby při načítání dat'} onClose={() => setIsErrorsDialogOpen(false)} escapeKeyClose outsideClickClose>
                    {/* DialogBody scrolluje sám (overflow-y: auto), hlavička a patička zůstávají vidět. */}
                    <DialogBody>
                        <div className={'error-dialog-content'}>
                            <Callout intent={'danger'} title={formatErrorCount(errorCount)}>
                                Některá data se nepodařilo načíst. Klepnutím na položku zobrazíte podrobnosti.
                            </Callout>
                            <div className={'error-dialog-list'}>{errorCards}</div>
                        </div>
                    </DialogBody>
                    <DialogFooter>
                        <Button style={'link'} onClick={() => setIsErrorsDialogOpen(false)}>
                            Zavřít
                        </Button>
                    </DialogFooter>
                </Dialog>
            )}
        </div>
    );
};
